import {
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { AuditLogs } from 'components/AccessModal/AuditLogs';
import { ErrorMessage } from 'components/ErrorMessage';
import { SuspendWithSpinner } from 'components/SuspendWithSpinner';
import { OverlayProps } from 'components/TaskOverlay';
import React from 'react';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useSafeRelay } from 'utils/graphClient';
import { VaultAvatar } from '../../apps/TenantApp/screens/Vaults/screens/VaultsManager/components/VaultAvatar';
import { AccessModal_Query } from './__generated__/AccessModal_Query.graphql';
import { PermissionRules } from './PermissionRules';
import { SpaceItemAccessDetails } from './SpaceItemAccessDetails';
import { VaultAccessDetails } from './VaultAccessDetails';
import { VaultItemAccessDetails } from './VaultItemAccessDetails';

const folderNodeQuery = graphql`
  query AccessModal_Query($id: ID!) {
    node(id: $id) {
      id
      ...PermissionRules_node
      ...useNodeSecurity_node
      ... on VaultItem {
        __typename
        ...VaultItemAccessDetails_node
      }
      ... on HasOwnerPermissionsInterface {
        __typename
        ...SpaceItemAccessDetails_node
      }
      ... on Vault {
        __typename
        name
        members {
          id
          ...UserAvatar_member
        }

        ...VaultAvatar_vault
        ...VaultAccessDetails_node
      }
      ... on HasAuditLogInterface {
        hasAuditLog: __typename
      }
    }
  }
`;

interface AccessModalProps extends OverlayProps {
  nodeId: string;
  defaultTab?: number;
}

export default function AccessModal({
  nodeId,
  defaultTab,
  ...props
}: AccessModalProps) {
  return (
    <Modal {...props}>
      <ModalOverlay />
      <ErrorBoundary fallback={<ErrorFallbackContent />}>
        <SuspendWithSpinner>
          <AccessModalContent nodeId={nodeId} defaultTab={defaultTab} />
        </SuspendWithSpinner>
      </ErrorBoundary>
    </Modal>
  );
}

const ErrorFallbackContent = () => {
  return (
    <ModalContent>
      <ModalHeader>{t`Error`}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <ErrorMessage description={t`Unable to load permissions`} />
      </ModalBody>
    </ModalContent>
  );
};

const AccessModalContent = ({
  nodeId,
  defaultTab,
}: {
  nodeId: string;
  defaultTab?: number;
}) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { node } = useLazyLoadQuery<AccessModal_Query>(folderNodeQuery, {
    id: nodeId,
  });

  if (!node || !node?.__typename) {
    throw new Error(t`Unable to load permissions`);
  }

  const { __typename, hasAuditLog } = node;
  const isVault: boolean = __typename === 'Vault';

  return (
    <ModalContent pb={3}>
      <ModalHeader>{isVault ? t`Vault access details` : t`Access`}</ModalHeader>
      <ModalCloseButton />

      <ModalBody py={0}>
        {isVault && (
          <HStack mb="4">
            <VaultAvatar vault={node} size="sm" />
            <Text fontWeight="bold">{node.name}</Text>
          </HStack>
        )}

        <Tabs defaultIndex={defaultTab || 0} isLazy>
          <TabList>
            <Tab>{t`Details`}</Tab>
            {!isVault && <Tab>{t`Rules`}</Tab>}
            {hasAuditLog && <Tab>{t`Audit logs`}</Tab>}
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <AccessDetails node={node} />
            </TabPanel>

            {!isVault && (
              <TabPanel px={0}>
                <Stack>
                  <PermissionRules node={node} />
                </Stack>
              </TabPanel>
            )}

            {hasAuditLog && (
              <TabPanel px={0}>
                <AuditLogs nodeId={node.id} />
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </ModalBody>
    </ModalContent>
  );
};
const AccessDetails = ({
  node,
}: {
  node: NonNullable<AccessModal_Query['response']['node']>;
}) => {
  switch (node?.__typename) {
    case 'VaultItem': {
      return <VaultItemAccessDetails node={node} />;
    }
    case 'Vault': {
      return <VaultAccessDetails node={node} />;
    }
    default:
      return <SpaceItemAccessDetails node={node} />;
  }
};
